import React, { useState } from 'react';
import { Save, Loader2, X, MapPin, Phone, LifeBuoy, Tag, UserCircle } from 'lucide-react';
import { atlasApi, AtlasUser, Colaborador, Contato, Endereco, Genero, LABEL_GENERO } from '../../shared/atlasApi';
import { inputCls, labelCls } from '../../shared/ListaEditavel';
import FeedbackBanner, { Feedback } from '../../shared/FeedbackBanner';

interface Props {
  user: AtlasUser;
  colaborador: Colaborador;
  podeEditar: boolean;
  onSalvo: () => void;
}

const EMAIL_RE = /^[^@]+@[^@]+\.[^@]+$/;

function mascararCep(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

const AbaPessoais: React.FC<Props> = ({ user, colaborador, podeEditar, onSalvo }) => {
  const e = colaborador.enriquecimento || {};
  const c0 = e.contato || {};
  const em0 = c0.emergencia || {};
  const en0 = e.endereco || {};

  const [contato, setContato] = useState<Contato & { emergencia: NonNullable<Contato['emergencia']> }>({
    celular: c0.celular || '',
    telefone_fixo: c0.telefone_fixo || '',
    email_pessoal: c0.email_pessoal || '',
    emergencia: {
      nome: em0.nome || '',
      parentesco: em0.parentesco || '',
      telefone: em0.telefone || '',
    },
  });
  const [endereco, setEndereco] = useState<Endereco>({
    cep: en0.cep || '',
    logradouro: en0.logradouro || '',
    numero: en0.numero || '',
    complemento: en0.complemento || '',
    bairro: en0.bairro || '',
    cidade: en0.cidade || '',
    uf: en0.uf || '',
  });
  const [tags, setTags] = useState<string[]>(Array.isArray(e.tags) ? [...e.tags] : []);
  const [tagInput, setTagInput] = useState('');
  const [genero, setGenero] = useState<Genero | ''>(e.genero || '');
  const [nomeSocial, setNomeSocial] = useState(e.nome_social || '');

  const [salvando, setSalvando] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const setC = (patch: Partial<typeof contato>) => setContato(p => ({ ...p, ...patch }));
  const setEm = (patch: Partial<typeof contato.emergencia>) =>
    setContato(p => ({ ...p, emergencia: { ...p.emergencia, ...patch } }));
  const setEn = (patch: Partial<Endereco>) => setEndereco(p => ({ ...p, ...patch }));

  const buscarCep = async () => {
    const limpo = (endereco.cep || '').replace(/\D/g, '');
    if (limpo.length !== 8) return;
    setBuscandoCep(true);
    setFeedback(null);
    try {
      const res = await atlasApi.buscarCep(user, limpo);
      setEn({
        logradouro: res.logradouro || endereco.logradouro,
        bairro: res.bairro || endereco.bairro,
        cidade: res.cidade || endereco.cidade,
        uf: res.uf || endereco.uf,
      });
    } catch (err: any) {
      setFeedback({ tipo: 'erro', msg: err?.message || 'CEP não encontrado.' });
    } finally {
      setBuscandoCep(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!tags.includes(t)) setTags(prev => [...prev, t]);
    setTagInput('');
  };
  const removerTag = (t: string) => setTags(prev => prev.filter(x => x !== t));

  const salvar = async () => {
    if (contato.email_pessoal && !EMAIL_RE.test(contato.email_pessoal)) {
      setFeedback({ tipo: 'erro', msg: 'E-mail pessoal inválido.' });
      return;
    }
    setSalvando(true);
    setFeedback(null);
    try {
      await atlasApi.putContato(user, colaborador.funccodi, {
        celular: contato.celular,
        telefone_fixo: contato.telefone_fixo,
        email_pessoal: contato.email_pessoal,
        emergencia: contato.emergencia,
      });
      await atlasApi.putEndereco(user, colaborador.funccodi, endereco);
      await atlasApi.putTags(user, colaborador.funccodi, tags);
      await atlasApi.editarColaborador(user, colaborador.funccodi, {
        genero: genero || null,
        nome_social: nomeSocial,
      });
      setFeedback({ tipo: 'ok', msg: 'Dados pessoais salvos com sucesso.' });
      onSalvo();
    } catch (err: any) {
      setFeedback({ tipo: 'erro', msg: err?.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const ro = !podeEditar;

  return (
    <div className="space-y-5">
      <FeedbackBanner feedback={feedback} />

      {/* Identificação */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider">
          <UserCircle className="w-4 h-4" /> Identificação
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Gênero</label>
            <select className={inputCls} disabled={ro} value={genero} onChange={ev => setGenero(ev.target.value as Genero | '')}>
              <option value="">—</option>
              {(Object.keys(LABEL_GENERO) as Genero[]).map(g => (
                <option key={g} value={g}>{LABEL_GENERO[g]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Nome social</label>
            <input className={inputCls} disabled={ro} value={nomeSocial} onChange={ev => setNomeSocial(ev.target.value)} />
          </div>
        </div>
      </section>

      {/* Contato */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider">
          <Phone className="w-4 h-4" /> Contato
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Celular</label>
            <input className={inputCls} disabled={ro} value={contato.celular} onChange={ev => setC({ celular: ev.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Telefone fixo</label>
            <input className={inputCls} disabled={ro} value={contato.telefone_fixo} onChange={ev => setC({ telefone_fixo: ev.target.value })} />
          </div>
          <div>
            <label className={labelCls}>E-mail pessoal</label>
            <input className={inputCls} disabled={ro} value={contato.email_pessoal} onChange={ev => setC({ email_pessoal: ev.target.value })} />
          </div>
        </div>
      </section>

      {/* Emergência */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider">
          <LifeBuoy className="w-4 h-4" /> Contato de emergência
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Nome</label>
            <input className={inputCls} disabled={ro} value={contato.emergencia.nome} onChange={ev => setEm({ nome: ev.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Parentesco</label>
            <input className={inputCls} disabled={ro} value={contato.emergencia.parentesco} onChange={ev => setEm({ parentesco: ev.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Telefone</label>
            <input className={inputCls} disabled={ro} value={contato.emergencia.telefone} onChange={ev => setEm({ telefone: ev.target.value })} />
          </div>
        </div>
      </section>

      {/* Endereço */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider">
          <MapPin className="w-4 h-4" /> Endereço
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <label className={labelCls}>CEP</label>
            <div className="relative">
              <input
                className={inputCls}
                disabled={ro}
                placeholder="00000-000"
                value={endereco.cep}
                onChange={ev => setEn({ cep: mascararCep(ev.target.value) })}
                onBlur={buscarCep}
              />
              {buscandoCep && <Loader2 className="w-4 h-4 animate-spin text-purple-500 absolute right-3 top-1/2 -translate-y-1/2" />}
            </div>
          </div>
          <div className="md:col-span-3">
            <label className={labelCls}>Logradouro</label>
            <input className={inputCls} disabled={ro} value={endereco.logradouro} onChange={ev => setEn({ logradouro: ev.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Número</label>
            <input className={inputCls} disabled={ro} value={endereco.numero} onChange={ev => setEn({ numero: ev.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Complemento</label>
            <input className={inputCls} disabled={ro} value={endereco.complemento} onChange={ev => setEn({ complemento: ev.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Bairro</label>
            <input className={inputCls} disabled={ro} value={endereco.bairro} onChange={ev => setEn({ bairro: ev.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Cidade</label>
            <input className={inputCls} disabled={ro} value={endereco.cidade} onChange={ev => setEn({ cidade: ev.target.value })} />
          </div>
          <div>
            <label className={labelCls}>UF</label>
            <input className={inputCls} disabled={ro} maxLength={2} value={endereco.uf} onChange={ev => setEn({ uf: ev.target.value.toUpperCase().slice(0, 2) })} />
          </div>
        </div>
      </section>

      {/* Tags */}
      <section className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider">
          <Tag className="w-4 h-4" /> Tags
        </div>
        <div className="flex flex-wrap gap-2">
          {tags.length === 0 && <span className="text-sm text-gray-400 italic">Nenhuma tag.</span>}
          {tags.map(t => (
            <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
              {t}
              {podeEditar && (
                <button type="button" onClick={() => removerTag(t)} className="hover:text-purple-900">
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
        {podeEditar && (
          <input
            className={inputCls}
            placeholder="Digite uma tag e pressione Enter"
            value={tagInput}
            onChange={ev => setTagInput(ev.target.value)}
            onKeyDown={ev => {
              if (ev.key === 'Enter') { ev.preventDefault(); addTag(); }
            }}
          />
        )}
      </section>

      {podeEditar && (
        <div className="flex justify-end pt-1">
          <button
            onClick={salvar}
            disabled={salvando}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition"
          >
            {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar dados pessoais
          </button>
        </div>
      )}
    </div>
  );
};

export default AbaPessoais;
